import React, { useState, useRef, useEffect } from 'react';
import styles from './CustomSelect.module.css';
import errorIcon from '../../utils/images/error-icon.svg';
import { Heading, TypographyColor, TypographyType } from '../typography/Heading';
import { Body, BodyColor, BodyType } from '../typography/Body';

export enum CustomSelectSize {
  small,
  medium,
  large,
}

interface CustomSelectProps<T> {
  options: T[];
  onChange?: (event: { target: { value: any; name?: string } }) => void;
  value: any;
  labelKey: keyof T;
  valueKey: keyof T;
  size?: CustomSelectSize;
  disabled?: boolean;
  className?: string;
  name?: string | string[];
  placeholder?: string;
  mbp?: boolean;
}

const getSizeClass = (size: CustomSelectSize): string => {
  switch (size) {
    case CustomSelectSize.small:
      return styles.select_button_sm;
    case CustomSelectSize.medium:
      return styles.select_button_md;
    case CustomSelectSize.large:
      return styles.select_button_lg;
    default:
      return styles.select_button_md;
  }
};

export const CustomSelect = <T extends Record<string, any>>({
  options,
  onChange,
  value,
  labelKey,
  valueKey,
  disabled = false,
  className = '',
  size = CustomSelectSize.medium,
  name,
  placeholder = 'Select',
  mbp,
}: CustomSelectProps<T>): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(
    Array.isArray(name) ? name[0] : ''
  );
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const tabbedOptions = Array.isArray(name)
    ? name.reduce((acc, type) => {
      acc[type] = options.filter(opt => opt?.geotype === type);
      return acc;
    }, {} as Record<string, T[]>)
    : {};

  // const selectedOption = Array.isArray(name)
  //   ? (tabbedOptions[activeTab] || []).find(opt => opt[valueKey] === value)
  //   : options.find(opt => opt[valueKey] === value);
  
  const selectedOption = Array.isArray(name)
    ? options.find(opt => opt[valueKey] === value)  // Search in ALL options
    : options.find(opt => opt[valueKey] === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: T): void => {
    if (onChange) {
      onChange({
        target: {
          value: option[valueKey],
          name: Array.isArray(name) ? activeTab : name,
        },
      });
    }
    setIsOpen(false);
    setSearchText('');
  };

  const toggleDropdown = (): void => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const filteredOptions = Array.isArray(name)
    ? (tabbedOptions[activeTab] || []).filter(opt =>
      String(opt?.[labelKey]).toLowerCase().includes(searchText.toLowerCase())
    )
    : options.filter(opt =>
      String(opt?.[labelKey]).toLowerCase().includes(searchText.toLowerCase())
    );

  const getListLabel = () => {
    if (typeof name === "string") {
      let label =
        name?.charAt(0).toUpperCase() +
        name?.slice(1).toLowerCase().replace('_', ' ');
      if (!label?.endsWith('s')) label += 's';
      return `${label} (${mbp ? filteredOptions.filter(data => data?.images?.length > 0).length : filteredOptions.length})`;
    }
    const active =
      activeTab?.charAt(0).toUpperCase() +
      activeTab?.slice(1).replace('_', '-');
    return `${active}s (${filteredOptions.length})`;
  };

  return (
    <div ref={dropdownRef} className={`${styles.select_wrapper} ${className}`}>
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`${styles.select_button} ${getSizeClass(size)} ${(mbp && selectedOption?.images && selectedOption?.images?.length === 0) ? 'border-rejected' : ''} ${disabled ? styles.select_disabled : ''}`}
      >
        <div className={styles.select_content}>
          {selectedOption ? (
            <>
              <span className={styles.select_text}>
                {String(selectedOption[labelKey])}
              </span>
              {selectedOption?.images?.length > 0
                ? (
                  <div className={styles.select_images}>
                    {selectedOption?.images?.map((img: string, idx: number) => (
                      <img
                        key={idx}
                        src={img}
                        alt=""
                        className={styles.select_image}
                      />
                    ))}
                  </div>
                )
                : (selectedOption?.images?.length === 0 && mbp)
                && <img
                  src={errorIcon}
                  alt="error"
                  className={styles.select_image}
                />
              }
            </>
          ) : (
            <span className={`${styles.select_text} ${styles.select_placeholder}`}>
              {placeholder}
            </span>
          )}
        </div>
        <div className={`${styles.select_arrow} ${isOpen ? styles.rotate : ''}`}></div>
      </button>

      {isOpen && <div className={styles.select_dropdown}>
        {Array.isArray(name) && (
          <div style={{ display: 'flex', borderBottom: '2px solid #EEE', marginBottom: 10 }}>
            {name.map(type => (
              <button
                key={type}
                type="button"
                className={`${styles.select_tab} ${activeTab === type ? styles.activeTab : ''}`}
                onClick={() => { setActiveTab(type); setSearchText(''); }}
              >
                {type?.charAt(0).toUpperCase() + type?.slice(1).replace('_', '-')}
              </button>
            ))}
          </div>
        )}
        {!(mbp && filteredOptions.filter(data => (data?.images?.length > 0)).length === 0) && <div className='px-3'>
          <div className={`border rounded d-flex align-items-center px-2 ${styles.search_box}`}>
            <span className="material-symbols-outlined fs-18">
              search
            </span>
            <input
              type="text"
              placeholder="Search"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className={styles.search_input}
            />
          </div>
          <div className={styles.select_label}>
            {getListLabel()}
          </div>
        </div>}
        {mbp
          ? filteredOptions.filter(data => (data?.images?.length > 0)).length === 0
            ? <div className='d-flex flex-column justify-content-start align-items-start gap-3 p-2'>
              <Heading
                title={`0 ${getListLabel()?.split('(')?.[0]} with Business Presence.`}
                type={TypographyType.h6}
                colour={TypographyColor.secondary}
              />
              <Body
                type={BodyType.p3}
                color={BodyColor.secondary}
              >
                Remove filters or change state to explore regions with business presence.
              </Body>
            </div>
            : filteredOptions.filter(data => (data?.images?.length > 0)).map((option: T, idx: number) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelect(option)}
                className={`${styles.select_option} ${option[valueKey] === value ? styles.active : ''}`}
              >
                <span className={styles.select_option_text}>
                  {String(option[labelKey])}
                </span>
                {option?.images?.length > 0 && (
                  <div className={styles.select_images}>
                    {option?.images?.map((img: string, imgIdx: number) => (
                      <img
                        key={imgIdx}
                        src={img}
                        alt=""
                        className={styles.select_image}
                      />
                    ))}
                  </div>
                )}
              </button>
            ))
          : filteredOptions.map((option: T, idx: number) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(option)}
              className={`${styles.select_option} ${option[valueKey] === value ? styles.active : ''}`}
            >
              <span className={styles.select_option_text}>
                {String(option[labelKey])}
              </span>
              {option?.images?.length > 0 && (
                <div className={styles.select_images}>
                  {option?.images?.map((img: string, imgIdx: number) => (
                    <img
                      key={imgIdx}
                      src={img}
                      alt=""
                      className={styles.select_image}
                    />
                  ))}
                </div>
              )}
            </button>
          ))}
      </div>
      }
    </div>
  );
};
